"use client";

import * as React from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { zodResolver } from "@hookform/resolvers/zod";
import { motion } from "framer-motion";
import { Building2, Loader2, Mail } from "lucide-react";
import { useForm } from "react-hook-form";
import QRCode from "react-qr-code";
import { SiweMessage } from "siwe";
import { z } from "zod";

import { rememberAuthMethod, trackEvent } from "@/lib/analytics";
import { signIn as authClientSignIn, useSession } from "@/lib/auth-client";
import { publicApi } from "@/lib/dpop-client";
import { FEXR_WALLET_ADDRESS_KEY, FEXR_WALLET_STORE } from "@/lib/fexr-wallet";
import { getPendingReferralCode } from "@/lib/pending-referral";
import { cn } from "@/lib/utils";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/use-toast";
import { useDPoPAuth } from "@/components/dpop-auth-context";
import { LoginMethodCard } from "@/components/forms/login-method-card";
import { useSearchParamsWrapper } from "@/components/search-params-provider";
import { Icons } from "@/components/shared/icons";

// ─── Wagmi safe-wrapper (identical to user-auth-form pattern) ──────────────
function useWagmiSafely() {
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { useAccount, useSignMessage } = require("wagmi");
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const account = useAccount();
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const signMessage = useSignMessage();
    return {
      address: account.address as `0x${string}` | undefined,
      chainId: account.chainId as number | undefined,
      isConnected: account.isConnected as boolean,
      signMessageAsync: signMessage.signMessageAsync,
    };
  } catch {
    return {
      address: undefined,
      chainId: undefined,
      isConnected: false,
      signMessageAsync: undefined,
    };
  }
}

const ConnectButton = dynamic(
  () =>
    import("@rainbow-me/rainbowkit").then((m) => ({
      default: m.ConnectButton,
    })),
  { ssr: false },
);

const ConnectModalBridge = dynamic(
  () => import("@/components/forms/connect-modal-bridge"),
  { ssr: false },
);

// ─── Types ─────────────────────────────────────────────────────────────────
type LoginMethod = "fexr" | "email" | "google" | "wallet" | "sso";

const emailSchema = z.object({
  email: z.string().email("Enter a valid email address"),
});
type EmailForm = z.infer<typeof emailSchema>;

const LAST_METHOD_KEY = "fexr_last_login_method";

// ─── Props ─────────────────────────────────────────────────────────────────
interface LoginPageClientProps {
  googleAvailable: boolean;
  mode?: "login" | "register";
}

// ─── Component ─────────────────────────────────────────────────────────────
// Carries a pending invite inside the post-sign-in address, so a magic link
// opened in another browser (or an OAuth/SSO round trip that loses storage)
// still lands with ?join=CODE for PendingReferralApplier to capture.
function withPendingInvite(path: string): string {
  const code = getPendingReferralCode();
  if (!code) return path;
  const [base = path, hash] = path.split("#");
  if (/[?&]join=/.test(base)) return path;
  const sep = base.includes("?") ? "&" : "?";
  return `${base}${sep}join=${code}${hash ? `#${hash}` : ""}`;
}

// ─── Sign in with Fexr Wallet ──────────────────────────────────────────────
// Request → wallet approves (QR, push + number, or same-phone link) → this
// tab collects the signed SIWE message and signs in with it. Flow and
// endpoints: docs/fexr-wallet-sign-in-plan.md, app/api/auth/wallet-login.

type WalletLoginRequest = {
  id: string;
  secret: string;
  code: string;
  qr: string;
  deepLink: string;
  expiresAt: number;
};

type WalletLoginOutcome =
  | { status: "approved"; message: string; signature: string }
  | { status: "rejected"; reason: "declined" | "code" }
  | { status: "expired" }
  | { status: "aborted" };

type FexrPhase =
  | "starting"
  | "ready"
  | "signing"
  | "declined"
  | "mismatch"
  | "stalled"
  | "error";

// Each request lives 2 minutes; after this many unanswered ones (10 min) the
// card stops minting codes until asked.
const FEXR_MAX_ROUNDS = 5;

async function postWalletLogin(
  body: Record<string, unknown>,
  signal: AbortSignal,
): Promise<Response> {
  return fetch("/api/auth/wallet-login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    cache: "no-store",
    signal,
  });
}

async function waitForWallet(
  request: WalletLoginRequest,
  signal: AbortSignal,
): Promise<WalletLoginOutcome> {
  while (!signal.aborted) {
    if (Date.now() > request.expiresAt + 5_000) return { status: "expired" };
    try {
      const res = await postWalletLogin(
        { action: "wait", id: request.id, secret: request.secret },
        signal,
      );
      const data = res.ok ? await res.json() : null;
      if (data?.status === "approved") return data;
      if (data?.status === "rejected") return data;
      if (data?.status === "expired") return { status: "expired" };
      if (!res.ok) await new Promise((r) => setTimeout(r, 2_000));
    } catch {
      if (signal.aborted) break;
      await new Promise((r) => setTimeout(r, 2_000));
    }
  }
  return { status: "aborted" };
}

function FexrWalletSignIn({
  active,
  onApproved,
}: {
  active: boolean;
  onApproved: (message: string, signature: string) => Promise<void>;
}) {
  const [hint, setHint] = React.useState<string | null>(null);
  const [isPhone, setIsPhone] = React.useState(false);
  const [ready, setReady] = React.useState(false);
  const [request, setRequest] = React.useState<WalletLoginRequest | null>(null);
  const [phase, setPhase] = React.useState<FexrPhase>("starting");
  const [attempt, setAttempt] = React.useState(0);
  const onApprovedRef = React.useRef(onApproved);
  onApprovedRef.current = onApproved;

  React.useEffect(() => {
    try {
      const stored = localStorage.getItem(FEXR_WALLET_ADDRESS_KEY);
      if (stored && /^0x[0-9a-fA-F]{40}$/.test(stored)) setHint(stored);
    } catch {
      /* restricted storage */
    }
    setIsPhone(
      window.matchMedia("(pointer: coarse)").matches && window.innerWidth < 768,
    );
    setReady(true);
  }, []);

  React.useEffect(() => {
    if (!active || !ready) return;
    const ctrl = new AbortController();
    const run = async () => {
      for (let round = 0; round < FEXR_MAX_ROUNDS; round++) {
        setPhase("starting");
        let next: WalletLoginRequest;
        try {
          const res = await postWalletLogin(
            { action: "start", address: hint ?? undefined },
            ctrl.signal,
          );
          if (!res.ok) throw new Error(String(res.status));
          next = await res.json();
        } catch {
          if (!ctrl.signal.aborted) setPhase("error");
          return;
        }
        if (ctrl.signal.aborted) return;
        setRequest(next);
        setPhase("ready");
        // Same phone: the link below opens the app, a push would be noise.
        if (hint && !isPhone) {
          void publicApi
            .post("/v1/walletconnect/login-prompt", {
              request_id: next.id,
              address: hint,
            })
            .catch(() => undefined);
        }

        const outcome = await waitForWallet(next, ctrl.signal);
        if (outcome.status === "aborted") return;
        if (outcome.status === "approved") {
          setPhase("signing");
          try {
            await onApprovedRef.current(outcome.message, outcome.signature);
          } catch {
            trackEvent("auth.failed", {
              method: "fexr_wallet",
              reason: "signin_failed",
            });
            setPhase("error");
          }
          return;
        }
        if (outcome.status === "rejected") {
          trackEvent("auth.failed", {
            method: "fexr_wallet",
            reason: outcome.reason === "code" ? "code_mismatch" : "declined",
          });
          setPhase(outcome.reason === "code" ? "mismatch" : "declined");
          return;
        }
        // expired → loop for a fresh code
      }
      setPhase("stalled");
    };
    void run();
    return () => ctrl.abort();
  }, [active, ready, hint, isPhone, attempt]);

  const retry = () => setAttempt((n) => n + 1);
  const forget = () => {
    try {
      localStorage.removeItem(FEXR_WALLET_ADDRESS_KEY);
    } catch {
      /* restricted storage */
    }
    setHint(null);
  };

  if (phase === "signing") {
    return (
      <p className="flex items-center gap-2 py-1 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Approved. Signing you in…
      </p>
    );
  }

  if (
    phase === "declined" ||
    phase === "mismatch" ||
    phase === "stalled" ||
    phase === "error"
  ) {
    const copy = {
      declined: "Declined in Fexr Wallet.",
      mismatch: "The number didn't match, so that request was cancelled.",
      stalled: "That code expired.",
      error: "Couldn't reach Fexr. Check your connection.",
    }[phase];
    return (
      <div className="flex flex-col items-start gap-2.5">
        <p className="text-sm text-muted-foreground">{copy}</p>
        <Button size="sm" variant="outline" onClick={retry}>
          {phase === "stalled" ? "Show a new code" : "Try again"}
        </Button>
      </div>
    );
  }

  const live = phase === "ready" && request;

  if (isPhone) {
    const store = /iPhone|iPad/.test(navigator.userAgent)
      ? FEXR_WALLET_STORE.ios
      : FEXR_WALLET_STORE.android;
    return (
      <div className="flex flex-col gap-2.5">
        <p className="text-xs text-muted-foreground">
          Approve in the Fexr app, then come back here. This page signs in on
          its own.
        </p>
        <a
          href={live ? request.deepLink : undefined}
          aria-disabled={!live}
          className={cn(
            buttonVariants({ size: "sm" }),
            "w-full",
            !live && "pointer-events-none opacity-60",
          )}
        >
          {live ? (
            "Open Fexr Wallet"
          ) : (
            <Loader2 className="h-4 w-4 animate-spin" />
          )}
        </a>
        <a
          href={store}
          className="text-center text-xs text-muted-foreground transition-colors duration-1 hover:text-foreground"
        >
          Don&apos;t have it? Get Fexr Wallet
        </a>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {hint && (
        <div className="flex flex-col gap-1">
          <p className="text-sm text-foreground">
            Check your phone for a sign-in request
          </p>
          <p className="text-xs text-muted-foreground">
            Enter{" "}
            <span className="font-figure text-base text-primary">
              {live ? request.code : "··"}
            </span>{" "}
            in Fexr Wallet to approve{" "}
            <span className="font-number">
              {hint.slice(0, 6)}…{hint.slice(-4)}
            </span>
            .{" "}
            <button
              type="button"
              onClick={forget}
              className="underline-offset-2 transition-colors duration-1 hover:text-foreground hover:underline"
            >
              Not you?
            </button>
          </p>
        </div>
      )}
      <div className="flex items-center gap-3">
        <div className="flex h-[7.5rem] w-[7.5rem] shrink-0 items-center justify-center rounded-md bg-foreground p-2">
          {live ? (
            <QRCode
              value={request.qr}
              size={104}
              className="duration-3 animate-in fade-in-0"
            />
          ) : (
            <Loader2 className="h-4 w-4 animate-spin text-background" />
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          {hint ? "Or scan: " : ""}open Fexr Wallet and tap{" "}
          <span className="text-foreground">Scan</span>, or point your phone
          camera at the code.
        </p>
      </div>
    </div>
  );
}

export function LoginPageClient({
  googleAvailable,
  mode = "login",
}: LoginPageClientProps) {
  const router = useRouter();
  const searchParams = useSearchParamsWrapper();
  const { setSessionKey } = useDPoPAuth();
  const { status: sessionStatus } = useSession();
  const { address, chainId, isConnected, signMessageAsync } = useWagmiSafely();
  const openConnectModalRef = React.useRef<(() => void) | undefined>(undefined);
  const handleConnectModalReady = React.useCallback(
    (open: (() => void) | undefined) => {
      openConnectModalRef.current = open;
    },
    [],
  );

  const [lastUsed, setLastUsed] = React.useState<LoginMethod | null>(null);
  // Fexr Wallet is the primary way in, so its QR is showing on arrival.
  const [expanded, setExpanded] = React.useState<LoginMethod | null>("fexr");
  const [loading, setLoading] = React.useState<LoginMethod | null>(null);
  const [emailSent, setEmailSent] = React.useState(false);
  const [ssoEmail, setSsoEmail] = React.useState("");
  const [ssoError, setSsoError] = React.useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setFocus,
    formState: { errors },
  } = useForm<EmailForm>({
    resolver: zodResolver(emailSchema),
  });

  const rawFrom = searchParams?.get("from") ?? "";
  const redirectTo =
    rawFrom.startsWith("/") && !rawFrom.startsWith("//")
      ? rawFrom
      : "/dashboard";
  const isAuthenticated = sessionStatus === "authenticated";
  const isRegister = mode === "register";
  // Arriving from a "Get Plus/Desk" button (fexr.io/plans or a paywall):
  // say where sign-in leads, so the extra step doesn't look like a detour.
  const checkoutPlan =
    /^\/dashboard\/billing\?(?:.*&)?plan=(plus|desk)(?:&|$)/.exec(
      redirectTo,
    )?.[1];

  // ── Read last-used method once on mount ───────────────────────────────
  React.useEffect(() => {
    try {
      const stored = localStorage.getItem(
        LAST_METHOD_KEY,
      ) as LoginMethod | null;
      if (stored) setLastUsed(stored);
    } catch {
      /* SSR or restricted storage */
    }
  }, []);

  // ── ?sso=1 opens the single sign-on card (link from Settings → Sign-in) ─
  React.useEffect(() => {
    if (searchParams?.get("sso")) setExpanded("sso");
  }, [searchParams]);

  // ── Auto-focus email input when card expands ───────────────────────────
  React.useEffect(() => {
    if (expanded === "email" && !emailSent) {
      setTimeout(() => setFocus("email"), 50);
    }
  }, [expanded, emailSent, setFocus]);

  // ── Show OAuth error when Auth.js redirects back with ?error= ─────────
  React.useEffect(() => {
    const error = searchParams?.get("error");
    if (!error) return;
    trackEvent("auth.failed", {
      method: "google",
      reason: error
        .toLowerCase()
        .replace(/[^a-z_]/g, "")
        .slice(0, 40),
    });
    const messages: Record<string, string> = {
      OAuthSignin:
        "Could not start sign-in. Check your OAuth client configuration.",
      OAuthCallback: "Sign-in was cancelled or failed.",
      OAuthAccountNotLinked:
        "This account is already linked to a different sign-in method.",
      Callback: "Sign-in callback failed. Please try again.",
    };
    toast({
      title: "Sign-in failed",
      description: messages[error] ?? `Sign-in error: ${error}`,
      variant: "destructive",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Auto sign-in after email verification link click ──────────────────
  React.useEffect(() => {
    const verifiedEmail = searchParams?.get("email");
    const loginToken = searchParams?.get("loginToken");
    const isVerified = searchParams?.get("verified") === "true";
    if (
      !verifiedEmail ||
      !loginToken ||
      !isVerified ||
      isAuthenticated ||
      loading
    )
      return;

    setLoading("email");
    authClientSignIn("email-link", {
      email: verifiedEmail,
      loginToken,
      redirect: false,
      callbackUrl: redirectTo,
    })
      .then((result) => {
        if (result?.ok || result?.url) {
          persistMethod("email");
          window.location.href = result?.url || redirectTo;
        } else {
          trackEvent("auth.failed", {
            method: "email_link",
            reason: "link_rejected",
          });
          toast({
            title: "Sign in failed",
            description:
              result?.error ||
              "Could not complete sign-in. Please request a new link.",
            variant: "destructive",
          });
        }
      })
      .catch(() =>
        toast({
          title: "Sign in failed",
          description: "Please try again.",
          variant: "destructive",
        }),
      )
      .finally(() => setLoading(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Wallet sign-in is triggered explicitly via the "Sign in" button below,
  // not auto-triggered from an effect — wallet popups must come from user gestures.

  // ─── Helpers ───────────────────────────────────────────────────────────
  function persistMethod(method: LoginMethod) {
    try {
      localStorage.setItem(LAST_METHOD_KEY, method);
    } catch {
      /* restricted */
    }
    setLastUsed(method);
  }

  function toggleExpand(method: LoginMethod) {
    setExpanded((prev) => (prev === method ? null : method));
  }

  // ─── Auth handlers ─────────────────────────────────────────────────────
  async function handleEmailSubmit(data: EmailForm) {
    setLoading("email");
    rememberAuthMethod("email_link", mode, !!rawFrom);
    try {
      const res = await fetch("/api/auth/email/send-magic-link", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: data.email.toLowerCase(),
          from: withPendingInvite(redirectTo),
        }),
      });
      const result = await res.json();
      if (!res.ok && !result.success)
        throw new Error(result.error || "Failed to send email");

      if (result.magicLink && process.env.NODE_ENV === "development") {
        console.log("Magic link (dev):", result.magicLink);
      }

      persistMethod("email");
      setEmailSent(true);
      trackEvent("auth.email_link_sent", { mode });
    } catch (err: any) {
      trackEvent("auth.failed", {
        method: "email_link",
        reason: "send_failed",
      });
      toast({
        title: "Something went wrong",
        description: err.message,
        variant: "destructive",
      });
    } finally {
      setLoading(null);
    }
  }

  // Company single sign-on: find the workspace connection for the email's
  // domain, bind a DPoP key, and hand off to the identity provider. The
  // provider returns to /auth/enterprise/callback.
  async function handleSsoSubmit(e: React.FormEvent) {
    e.preventDefault();
    const email = ssoEmail.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setSsoError("Enter your work email.");
      return;
    }
    setLoading("sso");
    setSsoError(null);
    rememberAuthMethod("enterprise", mode, !!rawFrom);
    try {
      const found = await fetch(
        `/api/auth/enterprise/discover?email=${encodeURIComponent(email)}`,
      ).then((r) => r.json());
      if (!found?.enterprise) {
        setSsoError(
          "Single sign-on isn't set up for this email's domain. Use another way to sign in.",
        );
        trackEvent("auth.failed", { method: "enterprise", reason: "no_sso" });
        return;
      }
      const { getOrCreateDPoPKeyPair } = await import("@/lib/dpop-keystore");
      const pair = await getOrCreateDPoPKeyPair();
      const res = await fetch("/api/auth/enterprise/oidc/start", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, dpopJwk: pair.publicKeyJWK }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok || !body?.authorization_url)
        throw new Error(body?.error ?? "Couldn't start single sign-on");
      try {
        sessionStorage.setItem("fexr:sso-from", withPendingInvite(redirectTo));
      } catch {
        /* storage blocked */
      }
      persistMethod("sso");
      window.location.href = body.authorization_url;
    } catch (err: any) {
      setSsoError(err?.message ?? "Couldn't start single sign-on");
    } finally {
      setLoading(null);
    }
  }

  async function handleGoogleAuth() {
    setLoading("google");
    rememberAuthMethod("google", mode, !!rawFrom);
    try {
      const res = await fetch("/api/auth/csrf", { credentials: "include" });
      if (!res.ok)
        throw new Error("Could not start sign-in. Try refreshing the page.");
      const { csrfToken } = await res.json();

      // Form submission: browser handles the OAuth redirect natively.
      // Avoids CORS issues with fetch following cross-origin 302s.
      const form = document.createElement("form");
      form.method = "POST";
      form.action = "/api/auth/signin/google";
      for (const [name, value] of [
        ["csrfToken", csrfToken],
        ["callbackUrl", withPendingInvite(redirectTo)],
      ] as [string, string][]) {
        const inp = document.createElement("input");
        inp.type = "hidden";
        inp.name = name;
        inp.value = value;
        form.appendChild(inp);
      }
      document.body.appendChild(form);
      persistMethod("google");
      form.submit();
      // browser navigates away — keep spinner active
    } catch (err: any) {
      trackEvent("auth.failed", { method: "google", reason: "start_failed" });
      toast({
        title: "Authentication failed",
        description: err.message,
        variant: "destructive",
      });
      setLoading(null);
    }
  }

  // Both wallet paths end here: a SIWE message (JSON, server nonce) signed by
  // the wallet → fresh DPoP key → `credentials` sign-in. Returns the URL to
  // land on; throws when the sign-in is refused.
  async function signInWithSiwe(
    message: string,
    signature: string,
  ): Promise<string> {
    const { generateAndStoreDPoPKeyPair } = await import("@/lib/dpop-keystore");
    const dpopPair = await generateAndStoreDPoPKeyPair();
    setSessionKey({
      privateKey: dpopPair.privateKey,
      publicKey: dpopPair.publicKey,
      publicKeyJWK: dpopPair.publicKeyJWK,
    });
    const result = await authClientSignIn("credentials", {
      message,
      signature,
      dpopPublicKey: JSON.stringify(dpopPair.publicKeyJWK),
      redirect: false,
      callbackUrl: redirectTo,
    });
    if (result?.error || !(result?.ok || result?.url)) {
      throw new Error(result?.error || "Wallet sign-in failed");
    }
    return result?.url || redirectTo;
  }

  // The wallet signed on the phone; this tab only collects it.
  async function handleFexrWalletApproved(message: string, signature: string) {
    rememberAuthMethod("fexr_wallet", mode, !!rawFrom);
    const signer = String(JSON.parse(message)?.address ?? "");
    const landing = await signInWithSiwe(message, signature);
    try {
      if (/^0x[0-9a-fA-F]{40}$/.test(signer)) {
        localStorage.setItem(FEXR_WALLET_ADDRESS_KEY, signer);
      }
    } catch {
      /* restricted storage */
    }
    persistMethod("fexr");
    window.location.href = landing;
  }

  async function handleWalletAuth() {
    if (!isConnected || !address || !signMessageAsync) {
      toast({
        title: "Wallet not connected",
        description: "Please connect your wallet first.",
        variant: "destructive",
      });
      return;
    }
    setLoading("wallet");
    rememberAuthMethod("wallet", mode, !!rawFrom);
    try {
      // Fetch server-issued nonce (stored in KV, one-time use)
      const nonceRes = await fetch("/api/auth/nonce");
      if (!nonceRes.ok) throw new Error("Failed to fetch nonce");
      const { nonce } = await nonceRes.json();

      const issuedAt = new Date().toISOString();
      const resolvedChainId = chainId ?? 1;
      const siweMsg = new SiweMessage({
        domain: window.location.host,
        address,
        statement: "Sign in with Ethereum to Fexr",
        uri: window.location.origin,
        version: "1",
        chainId: resolvedChainId,
        nonce,
        issuedAt,
      });
      // Omit `account` — viem validates chain ID when account is passed,
      // which fails if the wallet is on any chain other than the SIWE chainId.
      const signature = await signMessageAsync({
        message: siweMsg.prepareMessage(),
      });

      const landing = await signInWithSiwe(
        JSON.stringify({
          domain: window.location.host,
          address,
          statement: "Sign in with Ethereum to Fexr",
          uri: window.location.origin,
          version: "1",
          chainId: resolvedChainId,
          nonce,
          issuedAt,
        }),
        signature,
      );
      persistMethod("wallet");
      window.location.href = landing;
      return;
    } catch (err: any) {
      trackEvent("auth.failed", {
        method: "wallet",
        reason: /reject|denied|cancel/i.test(String(err?.message ?? ""))
          ? "cancelled"
          : "siwe_failed",
      });
      toast({
        title: "Authentication failed",
        description: err.message || "Wallet sign-in failed",
        variant: "destructive",
      });
    }
    setLoading(null);
  }

  // ─── Method definitions ────────────────────────────────────────────────
  const methods = (
    [
      {
        id: "fexr" as LoginMethod,
        icon: (
          // eslint-disable-next-line @next/next/no-img-element
          <img src="/icons/pwa-192.png" alt="" className="h-4 w-4 rounded-sm" />
        ),
        title: isRegister
          ? "Continue with Fexr Wallet"
          : "Sign in with Fexr Wallet",
        subtitle: "Approve on your phone · no password",
        show: true,
        action: () => toggleExpand("fexr"),
        content: (
          <FexrWalletSignIn
            active={expanded === "fexr"}
            onApproved={handleFexrWalletApproved}
          />
        ),
      },
      {
        id: "wallet" as LoginMethod,
        icon: <Icons.ethereum className="h-4 w-4" />,
        title: "Other wallet",
        subtitle: "MetaMask, Rainbow, Coinbase & more",
        show: true,
        action: () => {
          // Opening the RainbowKit modal here is fine: the card click is a user
          // gesture. Only the SIWE signature must stay behind its own button.
          if (expanded !== "wallet" && !isConnected)
            openConnectModalRef.current?.();
          toggleExpand("wallet");
        },
        content: (
          <div className="flex flex-col items-start gap-2.5">
            <p className="text-xs text-muted-foreground">
              Connect any Ethereum-compatible wallet to continue.
            </p>
            {isConnected && address ? (
              <div className="flex w-full flex-col gap-2">
                <Button
                  size="sm"
                  className="w-full"
                  onClick={handleWalletAuth}
                  disabled={loading === "wallet"}
                >
                  {loading === "wallet" ? (
                    <span className="flex items-center gap-1.5">
                      <Icons.Spinner className="h-4 w-4 animate-spin" />
                      {isRegister ? "Creating account…" : "Signing in…"}
                    </span>
                  ) : (
                    <span>
                      {isRegister ? "Continue as" : "Sign in as"}{" "}
                      <span className="font-number">
                        {address.slice(0, 6)}…{address.slice(-4)}
                      </span>
                    </span>
                  )}
                </Button>
                <div className="flex items-center gap-2">
                  <div className="h-px flex-1 bg-border/50" />
                  <span className="text-xs text-muted-foreground">
                    or use a different wallet
                  </span>
                  <div className="h-px flex-1 bg-border/50" />
                </div>
                <div className="flex w-full justify-center">
                  <ConnectButton />
                </div>
              </div>
            ) : (
              <div className="flex w-full justify-center">
                <ConnectButton />
              </div>
            )}
          </div>
        ),
      },
      {
        id: "email" as LoginMethod,
        icon: <Mail className="h-4 w-4" />,
        title: isRegister ? "Sign up with email" : "Sign in with email",
        subtitle: "No password · wallet created automatically",
        show: true,
        action: () => toggleExpand("email"),
        content: (
          <form
            onSubmit={handleSubmit(handleEmailSubmit)}
            className="flex flex-col gap-2"
          >
            {emailSent ? (
              <p className="py-1 text-sm text-muted-foreground">
                {isRegister
                  ? "Account link sent. Check your inbox."
                  : "Sign-in link sent. Check your inbox."}
              </p>
            ) : (
              <>
                <Input
                  type="email"
                  placeholder="name@example.com"
                  autoComplete="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  disabled={loading === "email"}
                  {...register("email")}
                />
                {errors.email && (
                  <p className="text-xs text-destructive">
                    {errors.email.message}
                  </p>
                )}
                <Button
                  type="submit"
                  size="sm"
                  disabled={loading === "email"}
                  className="w-full"
                >
                  {loading === "email" ? (
                    <Icons.Spinner className="h-4 w-4 animate-spin" />
                  ) : isRegister ? (
                    "Send account link"
                  ) : (
                    "Send sign-in link"
                  )}
                </Button>
              </>
            )}
          </form>
        ),
      },
      {
        id: "sso" as LoginMethod,
        icon: <Building2 className="h-4 w-4" />,
        title: "Sign in with SSO",
        subtitle: "Your company's identity provider · Fexr Desk teams",
        show: !isRegister,
        action: () => toggleExpand("sso"),
        content: (
          <form onSubmit={handleSsoSubmit} className="flex flex-col gap-2">
            <Input
              type="email"
              placeholder="name@company.com"
              autoComplete="email"
              autoCapitalize="none"
              value={ssoEmail}
              onChange={(e) => setSsoEmail(e.target.value)}
              disabled={loading === "sso"}
            />
            {ssoError && <p className="text-xs text-destructive">{ssoError}</p>}
            <Button
              type="submit"
              size="sm"
              disabled={loading === "sso"}
              className="w-full"
            >
              {loading === "sso" ? (
                <Icons.Spinner className="h-4 w-4 animate-spin" />
              ) : (
                "Continue with SSO"
              )}
            </Button>
          </form>
        ),
      },
      {
        id: "google" as LoginMethod,
        icon: <Icons.google className="h-4 w-4" />,
        title: "Continue with Google",
        subtitle: "One tap · wallet created automatically",
        show: googleAvailable,
        action: handleGoogleAuth,
      },
    ] as Array<{
      id: LoginMethod;
      icon: React.ReactNode;
      title: string;
      subtitle: string;
      show: boolean;
      action: () => void;
      content?: React.ReactNode;
    }>
  ).filter((m) => m.show);

  // ── Sort: Fexr Wallet first, then last-used, then the rest ─────────────
  const methodOrder: Record<LoginMethod, number> = {
    fexr: -1,
    google: 0,
    email: 1,
    wallet: 2,
    sso: 3,
  };
  const sorted = [...methods].sort((a, b) => {
    if (a.id === "fexr" || b.id === "fexr") {
      return methodOrder[a.id] - methodOrder[b.id];
    }
    if (lastUsed && a.id === lastUsed) return -1;
    if (lastUsed && b.id === lastUsed) return 1;
    return methodOrder[a.id] - methodOrder[b.id];
  });

  // ─── Render ────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col gap-2.5">
      <ConnectModalBridge onReady={handleConnectModalReady} />
      {checkoutPlan && (
        <p className="pb-1 text-center text-sm text-muted-foreground">
          {isRegister ? "Create an account" : "Sign in"} to get{" "}
          <span className="text-foreground">
            Fexr {checkoutPlan === "plus" ? "Plus" : "Desk"}
          </span>
          . Checkout opens right after.
        </p>
      )}
      {sorted.map((method, i) => (
        <motion.div
          key={method.id}
          layout
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            delay: i * 0.055,
            duration: 0.22,
            ease: [0.4, 0, 0.2, 1],
          }}
        >
          <LoginMethodCard
            icon={method.icon}
            title={method.title}
            subtitle={method.subtitle}
            isLastUsed={method.id === lastUsed}
            isLoading={loading === method.id}
            isExpanded={expanded === method.id}
            onClick={method.action}
          >
            {method.content}
          </LoginMethodCard>
        </motion.div>
      ))}
    </div>
  );
}

/**
 * Log in ⇄ Create account link that keeps `?from=`, so switching forms on the
 * way to checkout (or any deep link) still lands there. Read after mount: the
 * auth pages are statically rendered.
 */
export function AuthSwitchLink({
  href,
  className,
  children,
}: {
  href: "/login" | "/register";
  className?: string;
  children: React.ReactNode;
}) {
  const [target, setTarget] = React.useState<string>(href);
  React.useEffect(() => {
    const from = new URLSearchParams(window.location.search).get("from");
    if (from && from.startsWith("/") && !from.startsWith("//"))
      setTarget(`${href}?from=${encodeURIComponent(from)}`);
  }, [href]);
  return (
    <Link href={target} className={className}>
      {children}
    </Link>
  );
}
